import { Fragment, useEffect, useState } from "react";
import {
  Plus, FileDown, Trash2, Loader2, Receipt, X, Pencil, Search, Minus,
  ArrowLeft, ChevronRight, ChevronDown, Copy, Building2,
  Check, CheckCircle2, Clock, Mail, MessageSquare, AlertCircle, FileCheck, Phone, MessageCircle,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { pdfUrl } from "../../lib/pdfUrl";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  loadDraft, saveDraft, clearDraft, DRAFT_RESUME_EVENT,
} from "../../lib/proformaDraft";
import { STANDARD_PRODUCTS } from "../../lib/productsCatalog";

const blankItem = {
  description: "", detail: "", hsn_code: "", gst_rate: 18, qty: 1, price: 0,
};

const DEFAULT_TERMS = [
  "Subject to Bangalore Jurisdiction.",
  "Goods once sold will not be taken back.",
  "Validity : 15 days from date of this offer.",
  "Replacement warranty : One year from the date of supply.",
  "Erection/Commng. : Actual, to provide Welder with Welding Machine & Electrodes to be provided at your end whenever required.",
  "Installation charges : Extra",
  "Transportation charges : Extra",
  "100% Advance Payment.",
];

const empty = {
  buyer_name: "", buyer_address: "", buyer_gstin: "",
  contact_person: "", contact_number: "",
  rfq_no: "", consignee: "", email: "", date: "",
  po_no: "", po_date: "",
  items: [{ ...blankItem }],
  installation_charge: 0, transportation_charge: 0, discount_pct: 0,
  cgst_pct: 9, sgst_pct: 9,
  apply_igst: false, igst_pct: 18,
  po_completed: false,
  remarks: "",
  terms_text: DEFAULT_TERMS.join("\n"),
};

function isMeaningful(f) {
  if (!f) return false;
  const txt = ["buyer_name", "buyer_address", "buyer_gstin", "contact_person",
    "contact_number", "rfq_no", "consignee", "email", "remarks", "po_no", "po_date"];
  if (txt.some((k) => String(f[k] || "").trim())) return true;
  if (f.po_completed) return true;
  if ((f.items || []).some((it) =>
    String(it.description || "").trim() || String(it.detail || "").trim() ||
    String(it.hsn_code || "").trim() || Number(it.price) > 0)) return true;
  return false;
}

function _money(n) {
  return Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function fmtDate(s) {
  if (!s) return "—";
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
}

function cleanPhone(p) {
  const digits = String(p || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

function openGmail(email, e) {
  if (e && e.stopPropagation) e.stopPropagation();
  if (e && e.preventDefault) e.preventDefault();
  if (!email) return;
  const clean = String(email).trim();
  const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(clean)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function normalizeCompany(name) {
  return (name || "").trim().toLowerCase();
}

function groupByCompany(items) {
  const map = new Map();
  for (const it of items) {
    const key = normalizeCompany(it.buyer_name) || "unknown";
    if (!map.has(key)) {
      map.set(key, {
        key,
        name: (it.buyer_name || "").trim() || "Unknown Company",
        invoices: [],
      });
    }
    map.get(key).invoices.push(it);
  }
  const groups = Array.from(map.values()).map((g) => {
    const arr = [...g.invoices].sort(
      (a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at)
    );
    const total = arr.reduce((acc, x) => acc + (Number(x.grand_total) || 0), 0);
    return {
      key: g.key,
      name: arr[0].buyer_name || g.name,
      latest: arr[0],
      invoices: arr,
      total,
    };
  });
  return groups.sort(
    (a, b) => new Date(b.latest.date || b.latest.created_at) - new Date(a.latest.date || a.latest.created_at)
  );
}

function getDaysElapsed(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const diffTime = now.setHours(0, 0, 0, 0) - d.setHours(0, 0, 0, 0);
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

function renderReminderBadge(days, isPoCompleted) {
  if (isPoCompleted) return null;
  if (days === null || days === undefined) return null;
  const count = Math.max(0, days);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200"
      title={`${count} days reminder`}
    >
      <Clock className="w-2.5 h-2.5 text-amber-600" />
      {count} {count === 1 ? "day" : "days"} reminder
    </span>
  );
}

function proformaToForm(p) {
  const termsArr = Array.isArray(p.terms) && p.terms.length
    ? p.terms
    : DEFAULT_TERMS;
  return {
    buyer_name: p.buyer_name || "",
    buyer_address: p.buyer_address || "",
    buyer_gstin: p.buyer_gstin || "",
    contact_person: p.contact_person || "",
    contact_number: p.contact_number || "",
    rfq_no: p.rfq_no || "",
    consignee: p.consignee || "",
    email: p.email || "",
    date: (p.date || "").slice(0, 10),
    po_no: p.po_no || "",
    po_date: (p.po_date || "").slice(0, 10),
    items: (p.items || []).map((it, idx) => ({
      sl_no: it.sl_no || String(idx + 1),
      description: it.description || "",
      detail: it.detail || "",
      hsn_code: it.hsn_code || "",
      gst_rate: it.gst_rate ?? 18,
      qty: it.qty ?? 1,
      price: it.price ?? 0,
    })),
    installation_charge: p.installation_charge ?? 0,
    transportation_charge: p.transportation_charge ?? 0,
    discount_pct: p.discount_pct ?? 0,
    cgst_pct: p.cgst_pct ?? 9,
    sgst_pct: p.sgst_pct ?? 9,
    apply_igst: Boolean(p.apply_igst),
    igst_pct: p.igst_pct ?? 18,
    po_completed: Boolean(p.po_completed),
    remarks: p.remarks || "",
    terms_text: termsArr.join("\n"),
  };
}

function formToPayload(f) {
  const terms = (f.terms_text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    buyer_name: f.buyer_name,
    buyer_address: f.buyer_address,
    buyer_gstin: f.buyer_gstin,
    contact_person: f.contact_person,
    contact_number: f.contact_number,
    rfq_no: f.rfq_no,
    consignee: f.consignee,
    email: f.email,
    date: f.date,
    po_no: (f.po_no || "").trim(),
    po_date: (f.po_date || "").trim(),
    items: f.items.map((it, idx) => ({
      sl_no: String(idx + 1),
      description: it.description,
      detail: it.detail || "",
      hsn_code: it.hsn_code,
      gst_rate: Number(it.gst_rate) || 0,
      qty: Number(it.qty) || 0,
      price: Number(it.price) || 0,
    })),
    installation_charge: Number(f.installation_charge) || 0,
    transportation_charge: Number(f.transportation_charge) || 0,
    discount_pct: Number(f.discount_pct) || 0,
    cgst_pct: Number(f.cgst_pct) || 0,
    sgst_pct: Number(f.sgst_pct) || 0,
    apply_igst: Boolean(f.apply_igst),
    igst_pct: Number(f.igst_pct) || 0,
    po_completed: Boolean(f.po_completed),
    remarks: f.remarks || "",
    terms,
  };
}

export default function ProformaInvoicesAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [poStatusFilter, setPoStatusFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [remarkDialog, setRemarkDialog] = useState({ open: false, id: null, text: "", currentPo: false });
  const [downloadingId, setDownloadingId] = useState(null);

  const downloadPiPdf = async (pi, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (e && e.preventDefault) e.preventDefault();
    const buyer = (pi?.buyer_name || form?.buyer_name || "").replace(/[\\/*?:"<>|]/g, "").trim().replace(/\s+/g, " ");
    const piNo = (pi?.pi_no || "UEE-PI").replace(/[\\/*?:"<>|]/g, "").trim().replace(/\s+/g, " ");
    const filename = buyer ? `${piNo}-${buyer}.pdf` : `${piNo}.pdf`;

    if (pi?.id) setDownloadingId(pi.id);
    try {
      let blob = null;

      // 1. Try download endpoint via Axios
      if (pi?.id) {
        try {
          const res = await api.get(`/proforma-invoices/${pi.id}/download`, { responseType: "blob" });
          if (res?.data && res.data.size > 100) {
            blob = res.data;
          }
        } catch (apiErr) {
          console.warn("Direct API download endpoint failed, trying static URL fallback", apiErr);
        }
      }

      // 2. Fallback to direct static fetch if blob not yet obtained
      if (!blob) {
        const url = pdfUrl(pi);
        if (!url) throw new Error("PDF url not available");
        const res = await fetch(url);
        if (!res.ok) throw new Error("PDF download failed");
        blob = await res.blob();
      }

      // 3. Trigger download with exact filename
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
      toast.success(`Downloaded: ${filename}`);
    } catch (err) {
      console.warn("Direct blob download failed, opening in browser tab", err);
      if (pi?.id) {
        const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
        window.open(`${backend}/api/proforma-invoices/${pi.id}/download`, "_blank");
      } else if (pi?.pdf_url) {
        window.open(pdfUrl(pi), "_blank");
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const load = () => api.get("/proforma-invoices").then((r) => setItems(r.data));

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, patch, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    try {
      await api.patch(`/proforma-invoices/${id}`, patch);
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openRemarkDialog = (pi, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setRemarkDialog({
      open: true,
      id: pi.id,
      text: pi.remarks || "",
      currentPo: pi.po_completed || false,
    });
  };

  const saveRemark = async () => {
    if (!remarkDialog.id) return;
    await updateStatus(remarkDialog.id, { remarks: remarkDialog.text });
    setRemarkDialog({ open: false, id: null, text: "", currentPo: false });
  };

  // Draft persistence
  useEffect(() => {
    const d = loadDraft();
    if (d && d.form && isMeaningful(d.form)) {
      setForm(d.form);
      setEditingId(d.editingId || null);
      if (d.open) setOpen(true);
    }
  }, []);

  useEffect(() => {
    const onResume = () => {
      const d = loadDraft();
      if (d && d.form) {
        setForm(d.form);
        setEditingId(d.editingId || null);
        setOpen(true);
      }
    };
    window.addEventListener(DRAFT_RESUME_EVENT, onResume);
    return () => window.removeEventListener(DRAFT_RESUME_EVENT, onResume);
  }, []);

  useEffect(() => {
    if (!isMeaningful(form)) {
      clearDraft();
      return;
    }
    saveDraft({ form, editingId, open });
  }, [form, editingId, open]);

  const openNew = () => {
    setEditingId(null);
    setForm({
      ...empty,
      date: new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  };

  const openFromProforma = (src) => {
    setEditingId(null);
    setForm({
      ...proformaToForm(src),
      date: new Date().toISOString().slice(0, 10),
      po_completed: false,
      remarks: "",
    });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm(proformaToForm(p));
    setOpen(true);
  };

  const minimize = () => {
    setOpen(false);
    toast.info("Proforma Invoice minimized — draft saved in browser.", { duration: 3000 });
  };

  const discard = () => {
    if (window.confirm("Discard draft? All unsaved inputs will be lost.")) {
      clearDraft();
      setForm(empty);
      setEditingId(null);
      setOpen(false);
    }
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const updateItem = (idx, patch) => {
    const next = [...form.items];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, items: next });
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { ...blankItem }] });
  };

  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  };

  // Calculations
  const computeTotals = () => {
    const itemsSub = (form.items || []).reduce((acc, it) => {
      return acc + (Number(it.qty) || 0) * (Number(it.price) || 0);
    }, 0);
    const install = Number(form.installation_charge) || 0;
    const transport = Number(form.transportation_charge) || 0;
    const preDiscount = itemsSub + install + transport;
    const discPct = Number(form.discount_pct) || 0;
    const discAmt = preDiscount * (discPct / 100);
    const subtotal = preDiscount - discAmt;

    let totalTax = 0;
    let igst = 0;
    let cgst = 0;
    let sgst = 0;
    if (form.apply_igst) {
      igst = subtotal * ((Number(form.igst_pct) || 0) / 100);
      totalTax = igst;
    } else {
      cgst = subtotal * ((Number(form.cgst_pct) || 0) / 100);
      sgst = subtotal * ((Number(form.sgst_pct) || 0) / 100);
      totalTax = cgst + sgst;
    }
    const grand = subtotal + totalTax;
    return {
      items_subtotal: itemsSub,
      installation: install,
      transportation: transport,
      discount: discAmt,
      discount_pct: discPct,
      subtotal,
      igst,
      cgst,
      sgst,
      total_tax: totalTax,
      grand,
    };
  };

  const t = computeTotals();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.buyer_name.trim()) {
      toast.error("Buyer name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (editingId) {
        const { data } = await api.put(`/proforma-invoices/${editingId}`, payload);
        toast.success(`Proforma Invoice ${data.pi_no} updated`);
      } else {
        const { data } = await api.post("/proforma-invoices", payload);
        toast.success(`Proforma Invoice ${data.pi_no} created`);
      }
      clearDraft();
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete proforma invoice ${p.pi_no}?`)) return;
    try {
      await api.delete(`/proforma-invoices/${p.id}`);
      toast.success("Proforma invoice deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const allGroups = groupByCompany(items);
  const activeGroup = selectedCompany
    ? allGroups.find((g) => g.key === selectedCompany)
    : null;

  const query = searchQuery.trim().toLowerCase();
  const matches = (it) => {
    if (!query) return true;
    const dev = (it.items || []).map((x) => x.description).join(" ").toLowerCase();
    return (
      (it.buyer_name || "").toLowerCase().includes(query) ||
      (it.pi_no || "").toLowerCase().includes(query) ||
      (it.po_no || "").toLowerCase().includes(query) ||
      (it.buyer_gstin || "").toLowerCase().includes(query) ||
      (it.contact_person || "").toLowerCase().includes(query) ||
      (it.contact_number || "").toLowerCase().includes(query) ||
      (it.email || "").toLowerCase().includes(query) ||
      (it.remarks || "").toLowerCase().includes(query) ||
      dev.includes(query)
    );
  };

  const statusMatch = (it) => {
    if (poStatusFilter === "Completed") return Boolean(it.po_completed);
    if (poStatusFilter === "Not Completed") return !it.po_completed;
    return true;
  };

  const groups = allGroups
    .map((g) => {
      const filteredInvoices = g.invoices.filter((it) => matches(it) && statusMatch(it));
      if (filteredInvoices.length === 0) return null;
      return {
        ...g,
        invoices: filteredInvoices,
        latest: filteredInvoices[0],
      };
    })
    .filter(Boolean);

  return (
    <div data-testid="admin-proforma-page">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-black text-slate-950 flex items-center gap-2">
            <FileCheck className="w-7 h-7 text-amber-500" /> Proforma Invoices
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            GST Proforma Invoices with P.O. Number &amp; P.O. Date tracking — auto-numbered (UEE-301).
          </p>
        </div>
        <Button data-testid="new-proforma-btn" onClick={() => openNew()} className="rounded-none btn-amber font-semibold h-10">
          <Plus className="w-4 h-4 mr-2" /> New Proforma Invoice
        </Button>
      </div>

      {!activeGroup && (
        <>
          {/* Search bar & PO Status Filter */}
          <div className="bg-white border border-slate-200 p-3 mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by company, PI No, PO No, GSTIN, product..."
                className="pl-9 rounded-none h-9 text-sm"
                data-testid="proforma-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
              {["all", "Completed", "Not Completed"].map((st) => (
                <button
                  key={st}
                  onClick={() => setPoStatusFilter(st)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                    poStatusFilter === st
                      ? "bg-slate-900 text-white shadow-xs"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {st === "all" ? "All PO Statuses" : st}
                </button>
              ))}
            </div>

            <span className="text-xs text-slate-500 font-mono">
              {query || poStatusFilter !== "all"
                ? `${groups.length} matching companies (${items.filter((it) => matches(it) && statusMatch(it)).length} invoices)`
                : `${allGroups.length} companies · ${items.length} proforma invoices`}
            </span>
          </div>

          {/* Companies overview table */}
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-600">
                <tr>
                  <th className="text-left p-3 font-semibold">Company / Buyer</th>
                  <th className="text-left p-3 font-semibold">Latest PI No</th>
                  <th className="text-left p-3 font-semibold">Latest Date &amp; Reminder</th>
                  <th className="text-left p-3 font-semibold">Latest P.O. Details</th>
                  <th className="text-center p-3 font-semibold">Invoices</th>
                  <th className="text-center p-3 font-semibold">PO Status</th>
                  <th className="text-left p-3 font-semibold">Contact Person &amp; No.</th>
                  <th className="text-right p-3 font-semibold">Total Value (₹)</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr
                    key={g.key}
                    onClick={() => { setSelectedCompany(g.key); setExpandedId(null); }}
                    className="border-t border-slate-200 hover:bg-amber-50/50 cursor-pointer transition-colors"
                    data-testid={`company-row-${g.key}`}
                  >
                    <td className="p-3">
                      <div className="font-bold text-slate-900 flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-amber-500 shrink-0" />
                        <span className="truncate max-w-[220px]">{g.name}</span>
                      </div>
                      {g.latest.buyer_address && (
                        <div className="text-xs text-slate-500 truncate max-w-[240px] mt-0.5">
                          {g.latest.buyer_address}
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs font-bold text-amber-700">{g.latest.pi_no}</td>
                    <td className="p-3">
                      <div className="font-mono text-xs font-medium">{fmtDate(g.latest.date)}</div>
                      <div className="mt-1">{renderReminderBadge(getDaysElapsed(g.latest.date), g.latest.po_completed)}</div>
                    </td>
                    <td className="p-3 text-xs">
                      {g.latest.po_no ? (
                        <div className="bg-rose-50/90 border border-rose-200/90 rounded px-2.5 py-1.5 max-w-[210px]">
                          <div className="font-mono font-extrabold text-[12px] text-rose-950 truncate">
                            <span className="text-rose-700 font-extrabold">PO:</span> {g.latest.po_no}
                          </div>
                          {g.latest.po_date && (
                            <div className="text-[11px] font-bold text-rose-800 font-mono mt-0.5">
                              <span className="text-rose-600 font-semibold">Date:</span> {fmtDate(g.latest.po_date)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 font-mono">—</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-700">
                        {g.invoices.length}
                      </span>
                    </td>
                    <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={g.latest.po_completed ? "Completed" : "Not Completed"}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateStatus(g.latest.id, { po_completed: e.target.value === "Completed" }, e);
                        }}
                        className={`text-xs font-bold px-2.5 py-1 rounded-full border cursor-pointer focus:outline-none transition-colors ${
                          g.latest.po_completed
                            ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                            : "bg-slate-100 text-slate-700 border-slate-300"
                        }`}
                      >
                        <option value="Completed">Completed</option>
                        <option value="Not Completed">Not Completed</option>
                      </select>
                    </td>
                    <td className="p-3 text-xs" onClick={(e) => e.stopPropagation()}>
                      {g.latest.contact_person || g.latest.contact_number ? (
                        <div>
                          <div className="font-semibold text-slate-900 truncate max-w-[200px]" title={g.latest.contact_person}>
                            {g.latest.contact_person || "—"}
                          </div>
                          {g.latest.contact_number && (
                            <div className="flex items-center gap-1.5 mt-1">
                              <a
                                href={`tel:${g.latest.contact_number}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-amber-700 font-mono bg-slate-100 hover:bg-amber-100 px-1.5 py-0.5 rounded transition-colors"
                                title={`Call ${g.latest.contact_number}`}
                              >
                                <Phone className="w-3 h-3 text-amber-600 shrink-0" />
                                <span>{g.latest.contact_number}</span>
                              </a>
                              <a
                                href={`https://wa.me/${cleanPhone(g.latest.contact_number)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded transition-colors"
                                title={`WhatsApp chat with ${g.latest.contact_number}`}
                              >
                                <MessageCircle className="w-3 h-3 text-emerald-600 shrink-0" />
                                <span>WA</span>
                              </a>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="p-3 text-right font-mono font-bold text-slate-900">
                      ₹ {_money(g.total)}
                    </td>
                    <td className="p-3 text-slate-400"><ChevronRight className="w-4 h-4" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length === 0 && (
              <div className="p-12 text-center text-slate-500 text-sm">
                No proforma invoices yet. Click <strong>New Proforma Invoice</strong> to build one.
              </div>
            )}
            {items.length > 0 && groups.length === 0 && (
              <div className="p-8 text-center text-slate-500 text-sm">
                No company matches <span className="font-mono font-semibold">"{searchQuery}"</span>.
                <button type="button" onClick={() => setSearchQuery("")} className="ml-2 underline hover:text-slate-700">
                  Clear search
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Selected Company detail view */}
      {activeGroup && (
        <div data-testid="company-proforma-detail">
          <button
            type="button"
            onClick={() => { setSelectedCompany(null); setExpandedId(null); }}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-3"
          >
            <ArrowLeft className="w-4 h-4" /> All Companies
          </button>

          <div className="bg-white border border-slate-200 p-5 mb-3 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Company</div>
              <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-500" /> {activeGroup.name}
              </h2>
              {activeGroup.latest.buyer_address && (
                <div className="text-xs text-slate-600 mt-1 whitespace-pre-line">{activeGroup.latest.buyer_address}</div>
              )}
              <div className="text-xs text-slate-500 mt-1">
                {[
                  activeGroup.latest.buyer_gstin && `GSTIN: ${activeGroup.latest.buyer_gstin}`,
                  activeGroup.latest.contact_person,
                  activeGroup.latest.contact_number,
                ].filter(Boolean).join("  ·  ")}
              </div>
              <div className="text-xs text-slate-700 mt-2 font-semibold">
                {activeGroup.invoices.length} invoice{activeGroup.invoices.length === 1 ? "" : "s"} · Total ₹ {_money(activeGroup.total)}
              </div>
            </div>
            <Button
              onClick={() => openFromProforma(activeGroup.latest)}
              className="rounded-none btn-amber font-semibold h-10"
            >
              <Plus className="w-4 h-4 mr-2" /> New Proforma Invoice for this company
            </Button>
          </div>

          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-600">
                <tr>
                  <th className="text-left p-3 font-semibold w-10">No.</th>
                  <th className="text-left p-3 font-semibold">PI No</th>
                  <th className="text-left p-3 font-semibold">Date &amp; Reminder</th>
                  <th className="text-left p-3 font-semibold">P.O. Number &amp; Date</th>
                  <th className="text-left p-3 font-semibold">Email</th>
                  <th className="text-left p-3 font-semibold">Devices</th>
                  <th className="text-center p-3 font-semibold">PO Status</th>
                  <th className="text-left p-3 font-semibold">Contact Person &amp; No.</th>
                  <th className="text-right p-3 font-semibold">Grand Total (₹)</th>
                  <th className="text-right p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeGroup.invoices.filter(statusMatch).map((pi, idx) => {
                  const serial = activeGroup.invoices.length - idx;
                  const isOpen = expandedId === pi.id;
                  const devices = (pi.items || []).map((x) => x.description).filter(Boolean).join(", ");
                  return (
                    <Fragment key={pi.id}>
                      <tr
                        onClick={() => setExpandedId(isOpen ? null : pi.id)}
                        className={`border-t border-slate-200 cursor-pointer hover:bg-amber-50/50 ${isOpen ? "bg-amber-50/40" : ""}`}
                      >
                        <td className="p-3 font-mono text-xs font-bold text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            {serial}
                          </span>
                        </td>
                        <td className="p-3 font-mono text-xs font-bold text-amber-700">{pi.pi_no}</td>
                        <td className="p-3">
                          <div className="font-mono text-xs font-medium">{fmtDate(pi.date)}</div>
                          <div className="mt-1">{renderReminderBadge(getDaysElapsed(pi.date), pi.po_completed)}</div>
                        </td>
                        <td className="p-3 text-xs">
                          {pi.po_no ? (
                            <div className="bg-rose-50/90 border border-rose-200/90 rounded px-2.5 py-1.5 max-w-[210px]">
                              <div className="font-mono font-extrabold text-[12px] text-rose-950 truncate">
                                <span className="text-rose-700 font-extrabold">PO:</span> {pi.po_no}
                              </div>
                              {pi.po_date && (
                                <div className="text-[11px] font-bold text-rose-800 font-mono mt-0.5">
                                  <span className="text-rose-600 font-semibold">Date:</span> {fmtDate(pi.po_date)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 font-mono">—</span>
                          )}
                        </td>
                        <td className="p-3 text-xs" onClick={(e) => e.stopPropagation()}>
                          {pi.email ? (
                            <a
                              href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(pi.email.trim())}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => openGmail(pi.email, e)}
                              className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-800 hover:underline max-w-[150px] truncate"
                              title={`Click to compose in Gmail for ${pi.email}`}
                            >
                              <Mail className="w-3 h-3 text-sky-500 shrink-0" />
                              <span className="truncate">{pi.email}</span>
                            </a>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="p-3 text-xs max-w-[220px]">
                          <div className="truncate" title={devices}>{devices || "—"}</div>
                        </td>
                        <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={pi.po_completed ? "Completed" : "Not Completed"}
                            onChange={(e) => {
                              e.stopPropagation();
                              updateStatus(pi.id, { po_completed: e.target.value === "Completed" }, e);
                            }}
                            className={`text-xs font-bold px-2.5 py-1 rounded-full border cursor-pointer focus:outline-none transition-colors ${
                              pi.po_completed
                                ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                                : "bg-slate-100 text-slate-700 border-slate-300"
                            }`}
                          >
                            <option value="Completed">Completed</option>
                            <option value="Not Completed">Not Completed</option>
                          </select>
                        </td>
                        <td className="p-3 text-xs" onClick={(e) => e.stopPropagation()}>
                          {pi.contact_person || pi.contact_number ? (
                            <div>
                              <div className="font-semibold text-slate-900 truncate max-w-[180px]" title={pi.contact_person}>
                                {pi.contact_person || "—"}
                              </div>
                              {pi.contact_number && (
                                <div className="flex items-center gap-1.5 mt-1">
                                  <a
                                    href={`tel:${pi.contact_number}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-amber-700 font-mono bg-slate-100 hover:bg-amber-100 px-1.5 py-0.5 rounded transition-colors"
                                    title={`Call ${pi.contact_number}`}
                                  >
                                    <Phone className="w-3 h-3 text-amber-600 shrink-0" />
                                    <span>{pi.contact_number}</span>
                                  </a>
                                  <a
                                    href={`https://wa.me/${cleanPhone(pi.contact_number)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded transition-colors"
                                    title={`WhatsApp chat with ${pi.contact_number}`}
                                  >
                                    <MessageCircle className="w-3 h-3 text-emerald-600 shrink-0" />
                                    <span>WA</span>
                                  </a>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="p-3 text-right font-mono font-semibold">{_money(pi.grand_total)}</td>
                        <td className="p-3 text-right space-x-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => openEdit(pi)}
                            className="inline-flex p-2 hover:bg-amber-50 text-amber-700"
                            title="Edit & regenerate PDF"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openFromProforma(pi)}
                            className="inline-flex p-2 hover:bg-sky-50 text-sky-700"
                            title="Copy as new proforma invoice"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          {pi.pdf_url && (
                            <button
                              type="button"
                              onClick={(e) => downloadPiPdf(pi, e)}
                              disabled={downloadingId === pi.id}
                              className="inline-flex p-2 hover:bg-slate-100 text-slate-700 disabled:opacity-50"
                              title={`Download ${pi.pi_no ? `${pi.pi_no}-` : "UEE-PI-"}${pi.buyer_name || "Customer"}.pdf`}
                            >
                              {downloadingId === pi.id ? (
                                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                              ) : (
                                <FileDown className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => del(pi)}
                            className="p-2 hover:bg-red-50 text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="bg-slate-50/70">
                          <td colSpan={10} className="p-4">
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-2 px-1 text-xs">
                              <div className="font-semibold text-slate-700">
                                Items &amp; Charges Breakdown:
                              </div>
                              <div className="font-mono text-slate-600 space-x-4">
                                {pi.po_no && <span><b>P.O. Number:</b> {pi.po_no}</span>}
                                {pi.po_date && <span><b>P.O. Date:</b> {fmtDate(pi.po_date)}</span>}
                              </div>
                            </div>
                            <table className="w-full text-xs bg-white border border-slate-200">
                              <thead className="bg-slate-100 uppercase tracking-wider">
                                <tr>
                                  <th className="text-left p-2 w-10">Sl</th>
                                  <th className="text-left p-2">Device / Description</th>
                                  <th className="text-left p-2">HSN</th>
                                  <th className="text-right p-2">Qty</th>
                                  <th className="text-right p-2">Rate (₹)</th>
                                  <th className="text-right p-2">Amount (₹)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(pi.items || []).map((it, k) => (
                                  <tr key={k} className="border-t border-slate-100">
                                    <td className="p-2 font-mono">{it.sl_no || k + 1}</td>
                                    <td className="p-2">
                                      <div className="font-semibold">{it.description}</div>
                                      {it.detail && <div className="text-slate-500 whitespace-pre-line">{it.detail}</div>}
                                    </td>
                                    <td className="p-2 font-mono">{it.hsn_code || "—"}</td>
                                    <td className="p-2 text-right font-mono">{it.qty}</td>
                                    <td className="p-2 text-right font-mono">{_money(it.price)}</td>
                                    <td className="p-2 text-right font-mono">{_money(it.amount ?? (Number(it.qty) || 0) * (Number(it.price) || 0))}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className="mt-3 ml-auto max-w-xs text-xs space-y-1">
                              {Number(pi.installation_charge) > 0 && (
                                <div className="flex justify-between"><span>Installation Charges</span><span className="font-mono">{_money(pi.installation_charge)}</span></div>
                              )}
                              {Number(pi.transportation_charge) > 0 && (
                                <div className="flex justify-between"><span>Transportation Charges</span><span className="font-mono">{_money(pi.transportation_charge)}</span></div>
                              )}
                              {Number(pi.discount_amount) > 0 && (
                                <div className="flex justify-between"><span>Discount ({pi.discount_pct}%)</span><span className="font-mono">- {_money(pi.discount_amount)}</span></div>
                              )}
                              <div className="flex justify-between"><span>Taxable value</span><span className="font-mono">{_money(pi.subtotal)}</span></div>
                              <div className="flex justify-between"><span>GST</span><span className="font-mono">{_money(pi.total_tax)}</span></div>
                              <div className="flex justify-between font-bold text-sm border-t border-slate-300 pt-1">
                                <span>Grand Total</span><span className="font-mono">₹ {_money(pi.grand_total)}</span>
                              </div>
                            </div>
                            {pi.remarks && (
                              <div className="mt-3 p-2.5 bg-amber-50/70 border border-amber-200/80 rounded text-xs text-slate-700 flex items-start gap-2 max-w-xl">
                                <MessageSquare className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                                <div>
                                  <span className="font-semibold text-amber-900">Remarks: </span>
                                  {pi.remarks}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Main Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={(v) => { if (v) setOpen(true); else minimize(); }}>
        <DialogContent
          className="max-w-5xl rounded-none max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-8">
              <DialogTitle className="font-heading flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-amber-500" />
                {editingId ? "Edit Proforma Invoice" : "New Proforma Invoice"}
              </DialogTitle>
              <button
                type="button"
                onClick={minimize}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 border border-slate-300 hover:border-slate-500 px-2.5 py-1"
                title="Minimize — data draft mein save rahega"
              >
                <Minus className="w-3.5 h-3.5" /> Minimize
              </button>
            </div>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-5 mt-2" data-testid="proforma-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Buyer (Bill to) — Name *</Label>
                <Input required value={form.buyer_name} onChange={setField("buyer_name")} className="rounded-none" data-testid="proforma-buyer-name" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Buyer Address</Label>
                <Textarea rows={2} value={form.buyer_address} onChange={setField("buyer_address")} className="rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Contact Person</Label>
                <Input value={form.contact_person} onChange={setField("contact_person")} className="rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Contact Number</Label>
                <Input value={form.contact_number} onChange={setField("contact_number")} className="rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Email</Label>
                <Input value={form.email} onChange={setField("email")} className="rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">RFQ No (optional)</Label>
                <Input value={form.rfq_no} onChange={setField("rfq_no")} className="rounded-none" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Buyer GSTIN / UIN</Label>
                <Input value={form.buyer_gstin} onChange={setField("buyer_gstin")} className="rounded-none font-mono" placeholder="29ABCDE1234F1Z5" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Consignee (Ship to)</Label>
                <Textarea rows={2} value={form.consignee} onChange={setField("consignee")} className="rounded-none" />
              </div>

              {/* DATE + PO NUMBER & PO DATE SECTION */}
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Proforma Invoice Date *</Label>
                <Input type="date" required value={form.date} onChange={setField("date")} className="rounded-none" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-amber-700">P.O. Number</Label>
                  <Input
                    value={form.po_no}
                    onChange={setField("po_no")}
                    placeholder="e.g. PO/2026/088"
                    className="rounded-none font-mono border-amber-300 focus:border-amber-500"
                    data-testid="proforma-po-no"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-amber-700">P.O. Date</Label>
                  <Input
                    type="date"
                    value={form.po_date}
                    onChange={setField("po_date")}
                    className="rounded-none border-amber-300 focus:border-amber-500"
                    data-testid="proforma-po-date"
                  />
                </div>
              </div>
            </div>

            {/* PO Status & Remarks */}
            <div className="border border-slate-200 p-3 bg-slate-50 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="proforma-po-completed" className="text-xs uppercase tracking-wider font-semibold">
                  PO Status
                </Label>
                <select
                  id="proforma-po-completed"
                  value={form.po_completed ? "Completed" : "Not Completed"}
                  onChange={(e) => setForm({ ...form, po_completed: e.target.value === "Completed" })}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg border cursor-pointer focus:outline-none transition-colors ${
                    form.po_completed
                      ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                      : "bg-slate-100 text-slate-700 border-slate-300"
                  }`}
                >
                  <option value="Completed">Completed</option>
                  <option value="Not Completed">Not Completed</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Remarks / Follow-up Notes</Label>
                <Textarea
                  rows={2}
                  value={form.remarks}
                  onChange={setField("remarks")}
                  placeholder="Follow-up notes, reminder details, client requirement updates..."
                  className="rounded-none text-xs bg-white mt-1"
                />
              </div>
            </div>

            {/* Items grid */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading font-bold uppercase tracking-wider text-sm">Items</h3>
                <div className="flex items-center gap-2">
                  <select
                    value=""
                    onChange={(e) => {
                      const selected = STANDARD_PRODUCTS.find((p) => p.name === e.target.value);
                      if (selected) {
                        setForm({
                          ...form,
                          items: [
                            ...form.items,
                            {
                              ...blankItem,
                              description: selected.name,
                              detail: selected.description,
                              hsn_code: selected.hsn_code,
                            },
                          ],
                        });
                      }
                    }}
                    className="text-xs bg-white border border-slate-300 text-slate-700 rounded h-8 px-2 font-medium focus:ring-1 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value="">⚡ + Add Standard Product...</option>
                    {STANDARD_PRODUCTS.map((prod) => (
                      <option key={prod.name} value={prod.name}>
                        {prod.name} (HSN: {prod.hsn_code})
                      </option>
                    ))}
                  </select>
                  <Button type="button" variant="outline" onClick={addItem} className="rounded-none h-8">
                    <Plus className="w-3 h-3 mr-1" /> Add row
                  </Button>
                </div>
              </div>
              <div className="border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-white text-xs uppercase">
                    <tr>
                      <th className="p-2 text-left w-10">#</th>
                      <th className="p-2 text-left">Product Description</th>
                      <th className="p-2 text-left w-24">HSN</th>
                      <th className="p-2 text-right w-20">GST %</th>
                      <th className="p-2 text-right w-20">Qty</th>
                      <th className="p-2 text-right w-28">Price</th>
                      <th className="p-2 text-right w-28">Amount</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => {
                      const amt = (Number(it.qty) || 0) * (Number(it.price) || 0);
                      return (
                        <tr key={i} className="border-t border-slate-200 align-top">
                          <td className="p-2 font-mono text-xs pt-3">{i + 1}</td>
                          <td className="p-1">
                            <select
                              value={STANDARD_PRODUCTS.some((p) => p.name === it.description) ? it.description : ""}
                              onChange={(e) => {
                                const selected = STANDARD_PRODUCTS.find((p) => p.name === e.target.value);
                                if (selected) {
                                  updateItem(i, {
                                    description: selected.name,
                                    detail: selected.description,
                                    hsn_code: selected.hsn_code,
                                  });
                                }
                              }}
                              className="w-full text-xs font-semibold bg-blue-50/70 hover:bg-blue-50 border border-blue-200 text-blue-900 rounded px-2 py-1 mb-1 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                              title="Select standard product to auto-fill details and HSN code"
                            >
                              <option value="">⚡ Select Standard Product...</option>
                              {STANDARD_PRODUCTS.map((prod) => (
                                <option key={prod.name} value={prod.name}>
                                  {prod.name} (HSN: {prod.hsn_code})
                                </option>
                              ))}
                            </select>
                            <Input
                              value={it.description}
                              required
                              placeholder="Product name (e.g. AFDSS Control Unit)"
                              onChange={(e) => {
                                const val = e.target.value;
                                const matched = STANDARD_PRODUCTS.find((p) => p.name.toLowerCase() === val.trim().toLowerCase());
                                if (matched) {
                                  updateItem(i, {
                                    description: val,
                                    detail: matched.description,
                                    hsn_code: matched.hsn_code,
                                  });
                                } else {
                                  updateItem(i, { description: val });
                                }
                              }}
                              className="rounded-none h-9 text-xs font-medium"
                            />
                            <Textarea
                              value={it.detail}
                              placeholder="Description / Specs (auto-filled, editable)…"
                              onChange={(e) => updateItem(i, { detail: e.target.value })}
                              className="rounded-none mt-1 text-xs"
                              rows={2}
                            />
                          </td>
                          <td className="p-1">
                            <Input value={it.hsn_code} onChange={(e) => updateItem(i, { hsn_code: e.target.value })} className="rounded-none h-9" />
                          </td>
                          <td className="p-1">
                            <Input type="number" step="1" min="0" value={it.gst_rate} onChange={(e) => updateItem(i, { gst_rate: e.target.value })} className="rounded-none h-9 text-right font-mono" />
                          </td>
                          <td className="p-1">
                            <Input type="number" step="1" min="1" value={it.qty} onChange={(e) => updateItem(i, { qty: e.target.value })} className="rounded-none h-9 text-right font-mono" />
                          </td>
                          <td className="p-1">
                            <Input type="number" step="any" min="0" value={it.price} onChange={(e) => updateItem(i, { price: e.target.value })} className="rounded-none h-9 text-right font-mono" />
                          </td>
                          <td className="p-2 text-right font-mono pt-3">{_money(amt)}</td>
                          <td className="p-1 text-right pt-2">
                            {form.items.length > 1 && (
                              <button type="button" onClick={() => removeItem(i)} className="p-1 text-red-600 hover:bg-red-50">
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Charges & Discount */}
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Installation Charges (₹)</Label>
                <Input type="number" step="0.01" value={form.installation_charge || ""} onChange={setField("installation_charge")} className="rounded-none" placeholder="₹ 0.00" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Transportation Charges (₹)</Label>
                <Input type="number" step="0.01" value={form.transportation_charge || ""} onChange={setField("transportation_charge")} className="rounded-none" placeholder="₹ 0.00" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Discount (%)</Label>
                <Input type="number" step="0.01" min="0" max="100" value={form.discount_pct || ""} onChange={setField("discount_pct")} className="rounded-none" placeholder="0" />
              </div>
            </div>

            {/* GST mode */}
            <div className="border border-slate-200 p-3 bg-slate-50">
              <div className="flex items-center gap-3 mb-3">
                <Checkbox
                  id="proforma-apply-igst"
                  checked={!!form.apply_igst}
                  onCheckedChange={(v) => setForm({ ...form, apply_igst: !!v })}
                />
                <Label htmlFor="proforma-apply-igst" className="text-xs uppercase tracking-wider font-semibold cursor-pointer">
                  Apply IGST (inter-state) — disables CGST/SGST
                </Label>
              </div>
              {form.apply_igst ? (
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">IGST %</Label>
                    <Input type="number" step="0.01" value={form.igst_pct} onChange={setField("igst_pct")} className="rounded-none font-mono" />
                  </div>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">CGST %</Label>
                    <Input type="number" step="0.01" value={form.cgst_pct} onChange={setField("cgst_pct")} className="rounded-none font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">SGST %</Label>
                    <Input type="number" step="0.01" value={form.sgst_pct} onChange={setField("sgst_pct")} className="rounded-none font-mono" />
                  </div>
                </div>
              )}
            </div>

            {/* Totals preview */}
            <div className="bg-slate-50 border border-slate-200 p-4">
              <div className="grid grid-cols-2 gap-x-12 gap-y-1 text-sm font-mono">
                <div>Items Subtotal</div><div className="text-right">{_money(t.items_subtotal)}</div>
                {t.installation > 0 && (<><div>Installation Charges</div><div className="text-right">{_money(t.installation)}</div></>)}
                {t.transportation > 0 && (<><div>Transportation Charges</div><div className="text-right">{_money(t.transportation)}</div></>)}
                {t.discount_pct > 0 && (
                  <>
                    <div>Discount @ {t.discount_pct}%</div>
                    <div className="text-right text-red-600">- {_money(t.discount)}</div>
                  </>
                )}
                <div className="font-semibold">Subtotal (taxable)</div><div className="text-right font-semibold">{_money(t.subtotal)}</div>
                {form.apply_igst ? (
                  <><div>IGST @ {form.igst_pct}%</div><div className="text-right">{_money(t.igst)}</div></>
                ) : (
                  <>
                    <div>CGST @ {form.cgst_pct}%</div><div className="text-right">{_money(t.cgst)}</div>
                    <div>SGST @ {form.sgst_pct}%</div><div className="text-right">{_money(t.sgst)}</div>
                  </>
                )}
                <div className="font-bold text-base pt-1 border-t border-slate-300">Grand Total</div>
                <div className="text-right font-bold text-base pt-1 border-t border-slate-300">₹ {_money(t.grand)}</div>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">
                  Terms &amp; Conditions (one per line — leave blank to omit section)
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-none h-7 text-xs"
                  onClick={() => setForm({ ...form, terms_text: DEFAULT_TERMS.join("\n") })}
                >
                  Reset to defaults
                </Button>
              </div>
              <Textarea
                rows={7}
                value={form.terms_text}
                onChange={setField("terms_text")}
                className="rounded-none font-mono text-xs"
                placeholder="One term per line…"
              />
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
              {isMeaningful(form) && (
                <span className="mr-auto text-xs text-slate-500">
                  Draft auto-saved — safe to navigate or refresh
                </span>
              )}
              {isMeaningful(form) && (
                <Button type="button" variant="outline" onClick={discard} className="rounded-none text-red-600 hover:bg-red-50">
                  Discard draft
                </Button>
              )}
              <Button type="button" variant="outline" onClick={minimize} className="rounded-none">
                Minimize
              </Button>
              <Button type="submit" disabled={saving} className="rounded-none btn-amber font-semibold min-w-32">
                {saving ? (
                  <span className="flex items-center"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving…</span>
                ) : editingId ? (
                  "Update Proforma Invoice"
                ) : (
                  "Create Proforma Invoice"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remark modal */}
      <Dialog open={remarkDialog.open} onOpenChange={(v) => setRemarkDialog({ ...remarkDialog, open: v })}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading">Follow-up Remarks / Notes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Notes</Label>
              <Textarea
                rows={4}
                value={remarkDialog.text}
                onChange={(e) => setRemarkDialog({ ...remarkDialog, text: e.target.value })}
                placeholder="e.g. Client requested sample, PO promised by next Friday..."
                className="rounded-none text-sm mt-1"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setRemarkDialog({ ...remarkDialog, open: false })} className="rounded-none">
                Cancel
              </Button>
              <Button type="button" onClick={saveRemark} className="rounded-none btn-amber font-semibold">
                Save Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
